import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Bell, Mail, ClipboardList, BellRing, FileBarChart, CalendarClock, LifeBuoy, Inbox, GraduationCap } from "lucide-react";

const ITEMS = [
  {
    key: "daily_digest",
    icon: Mail,
    title: "Daily digest email",
    description:
      "Each morning, get your reminders due today, stale follow-ups, your open tasks, and urgent maternal visits.",
  },
  {
    key: "task_assignments",
    icon: ClipboardList,
    title: "Task assignment emails",
    description: "Email me whenever a teammate assigns a new task to me.",
  },
  {
    key: "appointment_invites",
    icon: CalendarClock,
    title: "Appointment invites & reminders",
    description:
      "Email me when I'm scheduled for an appointment with a Care Seeker, including reschedules, cancellations, and a same-day morning reminder.",
  },
  {
    key: "followup_reminders",
    icon: BellRing,
    title: "Follow-up reminders",
    description: "Email reminders about Care Seekers I'm following up on.",
  },
  {
    key: "grant_reports",
    icon: FileBarChart,
    title: "Monthly grant report (Admin/ED)",
    description:
      "Receive the auto-generated monthly grant report PDF on the 1st of each month. Only delivered to admins and Supervisors.",
    restrictedTo: ["admin", "executive_director"],
  },
  {
    key: "tech_support_alerts",
    icon: LifeBuoy,
    title: "Tech support ticket alerts",
    description:
      "Email me when an IT support ticket is assigned to me or someone comments on a ticket I'm involved in.",
  },
  {
    key: "referral_alerts",
    icon: Inbox,
    title: "Inbound partner referrals (Admin/ED)",
    description:
      "Email me whenever a partner agency or community member sends a new referral via the public /refer page.",
    restrictedTo: ["admin", "executive_director"],
  },
  {
    key: "training_nudge",
    icon: GraduationCap,
    title: "Monday training nudge",
    description:
      "Email me every Monday morning with my remaining training items until I'm 100% complete.",
  },
];

export default function NotificationPrefsPanel() {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users/me/notification-prefs");
      setPrefs(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggle = async (key, current) => {
    setSaving(key);
    try {
      const { data } = await api.patch("/users/me/notification-prefs", { [key]: !current });
      setPrefs(data);
      toast.success(!current ? "You'll start receiving these." : "You'll no longer receive these.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(null);
    }
  };

  if (loading || !prefs) {
    return <div className="text-center py-12 text-[#1E3A8A]">Loading your preferences…</div>;
  }

  return (
    <div className="max-w-3xl space-y-4" data-testid="notification-prefs-panel">
      <div className="bg-white rounded-2xl border border-[#E5E5E0] p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-9 h-9 rounded-xl bg-[#E8ECE7] text-[#1E3A8A] flex items-center justify-center flex-shrink-0">
            <Bell className="w-4 h-4" strokeWidth={1.75} />
          </div>
          <div>
            <h2 className="font-display text-lg font-semibold text-[#0F172A]">Email notifications</h2>
            <p className="text-sm text-[#1E3A8A]">
              Manage which HarborWell Connect emails you receive at <strong>{user?.email}</strong>.
            </p>
          </div>
        </div>

        <ul className="divide-y divide-[#F0EFEB]">
          {ITEMS.map((item) => {
            if (item.restrictedTo && !item.restrictedTo.includes(user?.role)) return null;
            const enabled = prefs[item.key] !== false; // default true
            const isSaving = saving === item.key;
            const Icon = item.icon;
            return (
              <li
                key={item.key}
                className="flex items-start justify-between gap-4 py-4"
                data-testid={`pref-row-${item.key}`}
              >
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <Icon className="w-4 h-4 text-[#1E3A8A] mt-1 flex-shrink-0" strokeWidth={1.75} />
                  <div className="min-w-0">
                    <div className="font-medium text-[#0F172A] text-sm">{item.title}</div>
                    <div className="text-xs text-[#334155] mt-0.5">{item.description}</div>
                  </div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={enabled}
                  disabled={isSaving}
                  onClick={() => toggle(item.key, enabled)}
                  data-testid={`pref-toggle-${item.key}`}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                    enabled ? "bg-[#1E3A8A]" : "bg-[#E5E5E0]"
                  } ${isSaving ? "opacity-60" : ""}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      enabled ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <p className="text-xs text-[#1E3A8A] px-1">
        Note: critical security emails (password resets) are always delivered regardless of these
        preferences.
      </p>
    </div>
  );
}
